import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { X, Plus, Trash2, Sparkles } from "lucide-react";
import ScentPyramid from "@/components/ScentPyramid";

const FAMILY_OPTS = ["Woody", "Oriental", "Floral", "Fresh", "Gourmand", "Chypre", "Citrus", "Aquatic"];
const INTENSITIES = ["Light", "Moderate", "Bold", "Signature"];
const OCCASIONS = ["Daily", "Office", "Evening", "Formal", "Romantic", "Sport"];

export default function AIProfilePage() {
  const { authAxios } = useAuth();
  const { tr } = useLang();

  const [p, setP] = useState({
    fragrance_families: [], favorite_notes: [], disliked_notes: [],
    style: "", intensity: "", occasion: "", summary: "",
  });
  const [noteInput, setNoteInput] = useState("");
  const [dislikeInput, setDislikeInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pr, sp] = await Promise.all([
          authAxios.get("/profile"),
          authAxios.get("/ai-profiles/saved"),
        ]);
        setP((prev) => ({ ...prev, ...pr.data }));
        setSaved(sp.data || []);
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved AI profile?")) return;
    try {
      await authAxios.delete(`/ai-profiles/saved/${id}`);
      setSaved((s) => s.filter((x) => x.saved_id !== id));
    } catch (e) { toast.error("Error"); }
  };

  const toggle = (key, val) => {
    setP((prev) => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });
  };

  const addTag = (key, val, setVal) => {
    const v = val.trim();
    if (!v) return;
    setP((prev) => ({ ...prev, [key]: [...new Set([...(prev[key] || []), v])] }));
    setVal("");
  };

  const removeTag = (key, val) => setP((prev) => ({ ...prev, [key]: (prev[key] || []).filter((x) => x !== val) }));

  const save = async () => {
    setBusy(true);
    try {
      await authAxios.put("/profile", p);
      toast.success(tr("profile_saved"));
    } catch (e) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        <div className="pfi-overline">AI Fragrance Profile</div>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl">{tr("profile_title")}</h1>
        <p className="mt-2 text-[#a9a9b8] text-sm">{tr("profile_sub")}</p>
        <div className="mt-2 text-[11px] font-mono text-[#6c6c7a]">{tr("ai_not_official")}</div>

        <div className="mt-8 space-y-6">
          <Section title={tr("profile_families")}>
            <div className="flex flex-wrap gap-2">
              {FAMILY_OPTS.map((f) => {
                const on = p.fragrance_families?.includes(f);
                return (
                  <button
                    key={f}
                    data-testid={`family-${f}`}
                    onClick={() => toggle("fragrance_families", f)}
                    className={
                      "rounded-full px-4 py-2 text-xs border transition-colors " +
                      (on
                        ? "bg-[#d4af37] text-[#0a0a0c] border-[#d4af37]"
                        : "border-white/10 text-[#a9a9b8] hover:text-[#e6c687] hover:border-[#d4af37]/40")
                    }
                  >
                    {f}
                  </button>
                );
              })}
            </div>
          </Section>

          <TagSection title={tr("profile_favs")} value={noteInput} setValue={setNoteInput}
            tags={p.favorite_notes} onAdd={() => addTag("favorite_notes", noteInput, setNoteInput)}
            onRemove={(v) => removeTag("favorite_notes", v)} testidPrefix="fav" tr={tr} />

          <TagSection title={tr("profile_dislikes")} value={dislikeInput} setValue={setDislikeInput}
            tags={p.disliked_notes} onAdd={() => addTag("disliked_notes", dislikeInput, setDislikeInput)}
            onRemove={(v) => removeTag("disliked_notes", v)} testidPrefix="dislike" tr={tr} />

          <div className="grid md:grid-cols-2 gap-6">
            <Section title={tr("profile_intensity")}>
              <div className="flex flex-wrap gap-2">
                {INTENSITIES.map((v) => (
                  <button key={v} data-testid={`intensity-${v}`}
                    onClick={() => setP((prev) => ({ ...prev, intensity: v }))}
                    className={
                      "rounded-full px-4 py-2 text-xs border transition-colors " +
                      (p.intensity === v
                        ? "bg-[#d4af37] text-[#0a0a0c] border-[#d4af37]"
                        : "border-white/10 text-[#a9a9b8] hover:text-[#e6c687]")
                    }>
                    {v}
                  </button>
                ))}
              </div>
            </Section>
            <Section title={tr("profile_occasion")}>
              <div className="flex flex-wrap gap-2">
                {OCCASIONS.map((v) => (
                  <button key={v} data-testid={`occ-${v}`}
                    onClick={() => setP((prev) => ({ ...prev, occasion: v }))}
                    className={
                      "rounded-full px-4 py-2 text-xs border transition-colors " +
                      (p.occasion === v
                        ? "bg-[#d4af37] text-[#0a0a0c] border-[#d4af37]"
                        : "border-white/10 text-[#a9a9b8] hover:text-[#e6c687]")
                    }>
                    {v}
                  </button>
                ))}
              </div>
            </Section>
          </div>

          <Section title={tr("profile_style")}>
            <input
              data-testid="profile-style-input"
              value={p.style || ""}
              onChange={(e) => setP((prev) => ({ ...prev, style: e.target.value }))}
              placeholder="Minimalist · Warm · Confident · …"
              className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60"
            />
          </Section>

          <Section title={tr("profile_summary")}>
            <textarea
              data-testid="profile-summary-input"
              rows={4}
              value={p.summary || ""}
              onChange={(e) => setP((prev) => ({ ...prev, summary: e.target.value }))}
              className="w-full rounded-lg bg-[#181820] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#d4af37]/60"
            />
          </Section>

          <button data-testid="profile-save-btn" onClick={save} disabled={busy}
            className="pfi-btn-gold rounded-full px-6 py-3 text-sm disabled:opacity-60">
            {busy ? tr("loading") : tr("profile_save")}
          </button>
        </div>

        {/* Saved AI Fragrance Profiles */}
        <div className="mt-16">
          <div className="pfi-overline inline-flex items-center gap-2"><Sparkles size={12} /> {tr("saved_profiles_title")}</div>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl">{tr("saved_profiles_title")}</h2>
          <div className="mt-6 space-y-4">
            {saved.length === 0 ? (
              <div className="pfi-card p-6 text-sm text-[#a9a9b8]">{tr("saved_profiles_empty")}</div>
            ) : (
              saved.map((s) => (
                <div key={s.saved_id} className="pfi-card p-6" data-testid={`saved-profile-${s.saved_id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="pfi-overline">{new Date(s.created_at).toLocaleString()}</div>
                      <div className="mt-1 font-serif text-xl text-[#f8f8fc]">{s.title}</div>
                      {s.personality ? <div className="mt-1 text-sm text-[#a9a9b8]"><span className="text-[#e6c687] font-mono text-xs">{tr("ai_personality")}:</span> {s.personality}</div> : null}
                      {s.direction ? <div className="mt-1 text-sm text-[#a9a9b8]"><span className="text-[#e6c687] font-mono text-xs">{tr("ai_direction_line")}:</span> {s.direction}</div> : null}
                    </div>
                    <button data-testid={`saved-delete-${s.saved_id}`} onClick={() => deleteSaved(s.saved_id)} className="pfi-btn-outline rounded-full h-9 w-9 inline-flex items-center justify-center text-red-300 border-red-300/30 hover:bg-red-500/10" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-5">
                    <ScentPyramid
                      top={s.top}
                      heart={s.heart}
                      base={s.base}
                      labels={{ top: tr("ai_pyramid_top"), heart: tr("ai_pyramid_heart"), base: tr("ai_pyramid_base") }}
                    />
                  </div>
                  {s.interpretation ? (
                    <p className="mt-4 text-sm text-[#c8c8d4] leading-relaxed">{s.interpretation}</p>
                  ) : null}
                  <div className="mt-3 text-[11px] font-mono text-[#6c6c7a]">{tr("ai_not_official")}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pfi-card p-6">
      <div className="pfi-overline mb-4">{title}</div>
      {children}
    </div>
  );
}

function TagSection({ title, value, setValue, tags, onAdd, onRemove, testidPrefix, tr }) {
  return (
    <Section title={title}>
      <div className="flex flex-wrap gap-2 mb-3">
        {(tags || []).map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#181820] border border-white/10 px-3 py-1 text-xs text-[#e6c687]">
            {t}
            <button data-testid={`${testidPrefix}-remove-${t}`} onClick={() => onRemove(t)} className="hover:text-white"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid={`${testidPrefix}-input`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          className="flex-1 rounded-lg bg-[#181820] border border-white/10 px-4 py-2 text-sm outline-none focus:border-[#d4af37]/60"
          placeholder="Bergamot, Oud, Rose…"
        />
        <button data-testid={`${testidPrefix}-add-btn`} onClick={onAdd} className="pfi-btn-outline rounded-full px-4 py-2 text-xs inline-flex items-center gap-1">
          <Plus size={12} /> {tr("profile_add")}
        </button>
      </div>
    </Section>
  );
}
